import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Global axios interceptor: on 402 Payment Required, redirect to /paywall
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 402) {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/paywall') && !window.location.pathname.startsWith('/payment/')) {
        window.location.href = '/paywall';
      }
    }
    return Promise.reject(error);
  }
);

// Global axios interceptor: on 401 Unauthorized (expired access_token), try a
// single silent /auth/refresh and replay the original request. Prevents users
// from seeing 'Not Authenticated' after 15 min of idle.
//
// If the refresh itself 401s (refresh_token expired or revoked) the original
// request is un-recoverable — for AUTHENTICATED areas we redirect to /login
// with a ?next= return path. PUBLIC routes (landing, RSVP, accept-invite etc.)
// stay put — a 401 there is expected and must not boot the visitor.
let refreshInFlight = null;
const PUBLIC_PATH_PREFIXES = [
  '/', '/login', '/register', '/paywall', '/payment/', '/accept-invite', '/rsvp',
  '/forgot-password', '/reset-password', '/magic-login',
];
const isPublicPath = (pathname) => {
  if (pathname === '/') return true;
  return PUBLIC_PATH_PREFIXES.some((p) => p !== '/' && pathname.startsWith(p));
};
const redirectToLogin = () => {
  if (typeof window === 'undefined') return;
  const { pathname, search } = window.location;
  if (isPublicPath(pathname)) return;
  const next = encodeURIComponent(`${pathname}${search || ''}`);
  window.location.href = `/login?next=${next}`;
};
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error || {};
    if (!response || response.status !== 401 || !config) return Promise.reject(error);
    const url = config.url || '';
    // Don't intercept the pure auth endpoints themselves; don't retry twice.
    // /auth/me IS retried (common case: fresh tab with only refresh_token
    // cookie surviving — it must refresh silently, not redirect to /login).
    if (config._retried || url.endsWith('/auth/login') || url.endsWith('/auth/register')
        || url.endsWith('/auth/forgot-password') || url.endsWith('/auth/reset-password')) {
      return Promise.reject(error);
    }
    // If the refresh call itself 401s, the session is dead: bounce to /login.
    if (url.endsWith('/auth/refresh')) {
      redirectToLogin();
      return Promise.reject(error);
    }
    try {
      if (!refreshInFlight) {
        refreshInFlight = axios
          .post(`${API}/auth/refresh`, {}, { withCredentials: true })
          .finally(() => { refreshInFlight = null; });
      }
      await refreshInFlight;
    } catch {
      redirectToLogin();
      return Promise.reject(error);
    }
    config._retried = true;
    return axios(config);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Proactive refresh: while authenticated and the tab is visible, silently
  // refresh the 60-min access_token every 55 min so admins + users never hit
  // a mid-action 401 + retry round-trip. Pauses when the tab is hidden.
  useEffect(() => {
    const clear = () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
    if (!user) { clear(); return; }

    const tick = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      } catch {
        // 401/network → let the 401 interceptor handle the next actual request.
      }
    };
    // 55 minutes — gives a 5-min safety margin before the 60-min cookie expires.
    refreshTimerRef.current = setInterval(tick, 55 * 60 * 1000);

    // Also refresh when the tab regains focus after being hidden > 5 min
    // (people commonly background tabs for hours, then come back and click).
    let hiddenAt = null;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else if (hiddenAt && Date.now() - hiddenAt > 5 * 60 * 1000) {
        hiddenAt = null;
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clear();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user]);

  const login = async (email, password, remember = false) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password, remember }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  const refreshToken = async () => {
    try {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      await checkAuth();
    } catch {
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshToken, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
